import React, {useMemo, useRef, useEffect} from 'react'
import {Alert, Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner, UncontrolledTooltip} from 'reactstrap'
import classnames from 'classnames'
import {isLayerDpiInvalid} from '../context/SelectedVariantContext/helper'
import {useState} from 'react'
import {confirmModal} from '../../../shared/PFConfirmModal'
import Portal from '../../common/Portal'
import {toaster} from '../../../shared/PFToast'
import '../../../styles/index.scss'
import {createUpscalerWorker, upscaleWithWorker, upscaleOnMainThread} from '../../../webworkers/helpers/upscaler'
import {getUpscalerInstance} from '../../../utils/upscalerSingleton'
import {useSelectedVariantContext} from '../context/SelectedVariantContext'

const STATUS_UPSCALE = {
    LARGE: 'large',
    NONE: 'none',
    SMALL: 'small',
}

const DpiAlert = ({className, selectedLayer, dispatch}) => {
    const {
        state: {cropMode},
    } = useSelectedVariantContext()
    const [modal, setModal] = useState(false)
    const [dpi, setDpi] = useState()
    const [loading, setLoading] = useState(false)
    const workerRef = useRef(null)

    // Initialize worker on component mount
    useEffect(() => {
        workerRef.current = createUpscalerWorker()

        return () => {
            // Cleanup worker on unmount
            if (workerRef.current) {
                workerRef.current.terminate()
            }
            // Cleanup any blob URLs when component unmounts
            if (selectedLayer?.src && selectedLayer.src.startsWith('blob:')) {
                URL.revokeObjectURL(selectedLayer.src)
            }
        }
    }, [])

    const toggle = () => setModal(!modal)
    const invalidDpi = useMemo(() => {
        if (!selectedLayer) return false
        const {isInvalid, dpi} = isLayerDpiInvalid(selectedLayer)
        setDpi(dpi)
        return isInvalid
    }, [selectedLayer])

    const showAlert = useMemo(() => {
        const isSvgFile = selectedLayer?.imageType === 'image/svg+xml'
        return invalidDpi && !isSvgFile
    }, [invalidDpi, selectedLayer])

    const isShowButton = useMemo(() => {
        return dpi >= selectedLayer?.bgDpi / 2
    }, [dpi])

    const mpImage = useMemo(() => {
        return (selectedLayer?.height * selectedLayer?.width) / 1000000
    }, [selectedLayer])

    const upscaleStatus = useMemo(() => {
        if (dpi < selectedLayer?.bgDpi / 2) {
            return STATUS_UPSCALE.SMALL
        } else if (mpImage > 2) {
            return STATUS_UPSCALE.LARGE
        } else {
            return STATUS_UPSCALE.NONE
        }
    }, [mpImage, selectedLayer, dpi])

    const messageTooltip = useMemo(() => {
        if (upscaleStatus === STATUS_UPSCALE.LARGE) {
            return `Upscale DPI only available for images under 2MP. Current image: ${mpImage}MP.`
        }
    }, [upscaleStatus, selectedLayer])

    const handleScaleDpi = async () => {
        setLoading(true)

        const isPng = selectedLayer?.imageType === 'image/png' || selectedLayer?.src?.includes('image/png')

        try {
            let result

            // Try to use Web Worker first
            if (workerRef.current) {
                try {
                    result = await upscaleWithWorker(workerRef.current, selectedLayer?.src, selectedLayer, isPng)
                } catch (error) {
                    // If worker fails or fallback is required, use main thread
                    if (error.message === 'FALLBACK_REQUIRED' || error.message === 'Failed to create worker') {
                        console.log('Falling back to main thread processing')
                        // Get the cached upscaler instance
                        const upscaler = await getUpscalerInstance()
                        result = await upscaleOnMainThread(upscaler, selectedLayer?.src, selectedLayer, isPng)
                    } else {
                        throw error
                    }
                }
            } else {
                // No worker available, use main thread with cached upscaler
                const upscaler = await getUpscalerInstance()
                result = await upscaleOnMainThread(upscaler, selectedLayer?.src, selectedLayer, isPng)
            }

            // Cleanup old blob URL to prevent memory leak
            if (selectedLayer?.src && selectedLayer.src.startsWith('blob:')) {
                URL.revokeObjectURL(selectedLayer.src)
            }

            // Create blob URL and update layer
            const blobUrl = URL.createObjectURL(result.blob)

            dispatch({
                type: 'UPDATE_LAYER_ATTRIBUTE',
                payload: {
                    layer: {
                        ...selectedLayer,
                        src: blobUrl,
                        height: result.height,
                        width: result.width,
                        scaleX: selectedLayer.scaleX / 2,
                        scaleY: selectedLayer.scaleY / 2,
                        offsetX: selectedLayer.offsetX * 2,
                        offsetY: selectedLayer.offsetY * 2,
                    },
                    updateHistory: true,
                },
            })

            toaster({
                type: 'success',
                message: 'Upscale DPI successfully!',
                width: 247,
            })
        } catch (error) {
            console.error('Upscale error:', error)
            toaster({
                type: 'error',
                message: 'Upscale failed. Please try again.',
                width: 291,
            })
        } finally {
            setLoading(false)
        }
    }

    return (
        <>
            {showAlert && !cropMode && (
                <Alert color={'danger'} isOpen={true} className={classnames('DpiAlert', className)}>
                    <div>
                        <div>
                            <h4
                                className="alert-heading font-semibold text-md"
                                style={{fontSize: '16px', fontWeight: 500, lineHeight: '24px', marginBottom: '4px'}}
                            >
                                Low Image DPI
                            </h4>
                            <p className={'mb-0'} style={{fontSize: '14px', lineHeight: '20px'}}>
                                {upscaleStatus === STATUS_UPSCALE.NONE && (
                                    <>
                                        Scale down to {selectedLayer?.bgDpi} DPI, upload a higher-quality image, or use
                                        'Upscale DPI' to enhance your image for high-quality printing.
                                    </>
                                )}
                                {upscaleStatus === STATUS_UPSCALE.LARGE && (
                                    <>
                                        Scale down to {selectedLayer?.bgDpi} DPI, upload a higher-quality image, or use
                                        'Upscale DPI' to enhance your image for high-quality printing.
                                    </>
                                )}
                                {upscaleStatus === STATUS_UPSCALE.SMALL && (
                                    <>Scale down to {selectedLayer?.bgDpi / 2} DPI or upload a higher-quality image.</>
                                )}
                            </p>
                        </div>
                        {isShowButton && (
                            <div id="scaleDpi">
                                <Button
                                    outline
                                    onClick={() => {
                                        setModal(true)
                                    }}
                                    className="custom-btn-scale-dpi"
                                    disabled={upscaleStatus === STATUS_UPSCALE.LARGE}
                                    size="sm"
                                >
                                    Upscale DPI
                                </Button>
                                {messageTooltip && (
                                    <UncontrolledTooltip
                                        target="scaleDpi"
                                        placement="top"
                                        delay={{show: 0, hide: 0}}
                                        fade={false}
                                        container="body"
                                        popperClassName="custom-tooltip-dpi"
                                        style={{
                                            maxWidth: '320px',
                                            textAlign: 'left',
                                            paddingInline: '12px',
                                            fontFamily: 'Roboto',
                                            paddingBlock: '5px',
                                            lineHeight: '20px',
                                        }}
                                        modifiers={[
                                            {
                                                name: 'preventOverflow',
                                                options: {
                                                    boundary: 'viewport',
                                                },
                                            },
                                            {
                                                name: 'offset',
                                                options: {
                                                    offset: [0, 8],
                                                },
                                            },
                                            {
                                                name: 'flip',
                                                enabled: false,
                                            },
                                        ]}
                                    >
                                        {messageTooltip}
                                    </UncontrolledTooltip>
                                )}
                            </div>
                        )}
                    </div>
                </Alert>
            )}

            {/* Modal confirm */}
            <Modal isOpen={modal} toggle={toggle} className="custom-modal" style={{width: '560px !important'}}>
                <ModalHeader toggle={toggle} style={{padding: '24px'}}>
                    Upscale DPI
                </ModalHeader>
                <ModalBody style={{padding: '24px'}}>
                    <div>
                        Upscale will increase your image DPI from {dpi} to ~{dpi * 2}.
                    </div>
                    <div>Do you want to double the pixels to improve quality for printing or zooming?</div>
                </ModalBody>
                <ModalFooter style={{padding: '24px'}}>
                    <Button outline onClick={toggle} className="btn-default me-2" style={{width: '77px', margin: '0'}}>
                        No
                    </Button>{' '}
                    <Button
                        color="primary"
                        onClick={() => {
                            toggle()
                            handleScaleDpi()
                        }}
                        style={{width: '90px', margin: '0'}}
                    >
                        Yes
                    </Button>
                </ModalFooter>
            </Modal>

            {/* loading */}
            {loading && (
                <Portal containerId="dpi-alert">
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'column',
                            height: '100vh',
                            gap: '16px',
                        }}
                    >
                        <Spinner style={{color: '#007BFF'}} />
                        <div style={{color: 'rgba(48, 50, 56, 1)'}}>Upscale an image usually takes up to 1 minute</div>
                    </div>
                </Portal>
            )}
        </>
    )
}

export default DpiAlert

